/**
 * UI Manager
 * Handles UI-related functionality and DOM manipulation
 */

import { logger } from '../utils/logger'
import { Toast } from '../components/toast'

export class UIManager {
  private loadingModal: HTMLElement | null = null
  private toastContainer: HTMLElement | null = null
  private toast: Toast

  constructor() {
    this.initializeToastContainer()
    this.toast = new Toast()
    this.toast.init(this.toastContainer!)
  }

  /**
   * Initialize toast container
   */
  private initializeToastContainer(): HTMLElement {
    if (this.toastContainer) {
      return this.toastContainer
    }

    this.toastContainer = document.createElement('div')
    this.toastContainer.id = 'toast-container'
    this.toastContainer.className = 'toast-container'
    document.body.appendChild(this.toastContainer)

    logger.debug('Toast container initialized')
    return this.toastContainer
  }

  /**
   * Show loading modal
   */
  public showLoadingModal(title: string, message: string): void {
    this.hideLoadingModal() // Hide any existing modal

    this.loadingModal = document.createElement('div')
    this.loadingModal.className = 'loading-modal-overlay'
    this.loadingModal.innerHTML = `
      <div class="loading-modal">
        <div class="loading-spinner"></div>
        <h3>${title}</h3>
        <p>${message}</p>
      </div>
    `

    document.body.appendChild(this.loadingModal)
    logger.debug(`Loading modal shown: ${title}`)
  }

  /**
   * Update loading modal content
   */
  public updateLoadingModal(title: string, message: string): void {
    if (!this.loadingModal) {
      this.showLoadingModal(title, message)
      return
    }

    const titleElement = this.loadingModal.querySelector('h3')
    const messageElement = this.loadingModal.querySelector('p')

    if (titleElement) titleElement.textContent = title
    if (messageElement) messageElement.textContent = message

    logger.debug(`Loading modal updated: ${title}`)
  }

  /**
   * Hide loading modal
   */
  public hideLoadingModal(): void {
    console.log('🔍 UIManager.hideLoadingModal called, loadingModal:', this.loadingModal)
    if (this.loadingModal && this.loadingModal.parentNode) {
      console.log('🔍 Removing loading modal from DOM')
      this.loadingModal.parentNode.removeChild(this.loadingModal)
      this.loadingModal = null
      logger.debug('Loading modal hidden')
      console.log('✅ Loading modal removed from DOM')
    } else {
      console.log('⚠️ Loading modal not found or no parent node')
    }
  }

  /**
   * Show toast notification
   */
  public showToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', _duration: number = 5000): void {
    // Use the proper Toast component for consistent styling
    switch (type) {
      case 'success':
        this.toast.success(message, 'Success')
        break
      case 'error':
        this.toast.error(message, 'Error')
        break
      case 'warning':
        this.toast.warning(message, 'Warning')
        break
      default:
        this.toast.info(message, 'Info')
        break
    }
    
    logger.debug(`Toast shown: ${type} - ${message}`)
  }


  /**
   * Show confirmation dialog
   */
  public async showConfirmation(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div')
      overlay.className = 'confirmation-overlay'
      overlay.innerHTML = `
        <div class="confirmation-modal">
          <div class="confirmation-content">
            <h3>Confirm Action</h3>
            <p>${message}</p>
            <div class="confirmation-buttons">
              <button class="btn btn-secondary" id="confirm-cancel">Cancel</button>
              <button class="btn btn-primary" id="confirm-ok">OK</button>
            </div>
          </div>
        </div>
      `

      document.body.appendChild(overlay)

      const cancelBtn = overlay.querySelector('#confirm-cancel')
      const okBtn = overlay.querySelector('#confirm-ok')

      const cleanup = () => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay)
        }
      }

      cancelBtn?.addEventListener('click', () => {
        cleanup()
        resolve(false)
      })

      okBtn?.addEventListener('click', () => {
        cleanup()
        resolve(true)
      })

      // Close on overlay click
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          cleanup()
          resolve(false)
        }
      })

      logger.debug(`Confirmation dialog shown: ${message}`)
    })
  }

  /**
   * Auto-resize textarea
   */
  public autoResizeTextarea(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }

  /**
   * Sync card heights in a container
   */
  public syncCardHeights(container: HTMLElement): void {
    const cards = container.querySelectorAll('.card')
    if (cards.length === 0) return

    // Reset heights
    cards.forEach(card => {
      (card as HTMLElement).style.height = 'auto'
    })

    // Find max height
    let maxHeight = 0
    cards.forEach(card => {
      const height = (card as HTMLElement).offsetHeight
      if (height > maxHeight) {
        maxHeight = height
      }
    })

    // Apply max height to all cards
    cards.forEach(card => {
      (card as HTMLElement).style.height = maxHeight + 'px'
    })

    logger.debug(`Card heights synced: ${maxHeight}px`)
  }

  /**
   * Show save indicator
   */
  public showSaveIndicator(elementId: string, type: 'success' | 'error'): void {
    const element = document.getElementById(elementId)
    if (!element) return

    const indicator = document.createElement('div')
    indicator.className = `save-indicator save-indicator-${type}`
    indicator.textContent = type === 'success' ? '✅ Saved' : '❌ Save Failed'

    // Remove any existing indicator
    const existingIndicator = element.querySelector('.save-indicator')
    if (existingIndicator) {
      existingIndicator.remove()
    }

    element.appendChild(indicator)

    // Remove indicator after 3 seconds
    setTimeout(() => {
      if (indicator.parentNode) {
        indicator.parentNode.removeChild(indicator)
      }
    }, 3000)

    logger.debug(`Save indicator shown: ${type} for ${elementId}`)
  }

  /**
   * Create modal content wrapper
   */
  public createModalContent(title: string, content: string, footer?: string): HTMLElement {
    const modalContent = document.createElement('div')
    modalContent.className = 'modal-content'
    modalContent.innerHTML = `
      <div class="modal-header">
        <h2>${title}</h2>
        <button class="modal-close" type="button">&times;</button>
      </div>
      <div class="modal-body">
        ${content}
      </div>
      ${footer ? `<div class="modal-footer">${footer}</div>` : ''}
    `

    return modalContent
  }

  /**
   * Setup modal event listeners
   */
  public setupModalEventListeners(modalContent: HTMLElement, onClose?: () => void): void {
    const closeBtn = modalContent.querySelector('.modal-close')
    closeBtn?.addEventListener('click', () => {
      if (onClose) onClose()
    })

    // Close on escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose()
        document.removeEventListener('keydown', handleEscape)
      }
    }
    document.addEventListener('keydown', handleEscape)
  }

  /**
   * Get toast container
   */
  public getToastContainer(): HTMLElement | null {
    return this.toastContainer
  }

  /**
   * Show image modal
   */
  public showImageModal(imageSrc: string, title: string, subtitle: string): void {
    // Remove any existing image modal
    const existingModal = document.getElementById('image-modal')
    if (existingModal) {
      existingModal.remove()
    }

    // Create the image modal
    const modal = document.createElement('div')
    modal.id = 'image-modal'
    modal.className = 'image-modal-overlay'
    modal.innerHTML = `
      <div class="image-modal">
        <div class="image-modal-header">
          <h3>${title}</h3>
          <p>${subtitle}</p>
          <button class="image-modal-close" type="button">&times;</button>
        </div>
        <div class="image-modal-body">
          <img src="${imageSrc}" alt="${title}" class="image-modal-img">
        </div>
      </div>
    `

    document.body.appendChild(modal)

    // Add event listeners
    const closeBtn = modal.querySelector('.image-modal-close')
    closeBtn?.addEventListener('click', () => {
      modal.remove()
    })

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove()
      }
    })

    // Close on escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        modal.remove()
        document.removeEventListener('keydown', handleEscape)
      }
    }
    document.addEventListener('keydown', handleEscape)

    logger.debug(`Image modal shown: ${title}`)
  }

  /**
   * Cleanup UI elements
   */
  public cleanup(): void {
    this.hideLoadingModal()
    
    if (this.toastContainer && this.toastContainer.parentNode) {
      this.toastContainer.parentNode.removeChild(this.toastContainer)
      this.toastContainer = null
    }

    logger.debug('UI manager cleaned up')
  }

  /**
   * Update next button visibility based on selections
   */
  public updateNextButtonVisibility(workflowNav: any, appState: any): void {
    const currentStepName = workflowNav?.getStepName()
    if (currentStepName !== 'snippets') return

    const selections = appState.getSelections()
    const canAdvance = selections.length > 0
    const nextButton = document.getElementById('step-next-bottom')
    const navRight = document.querySelector('.step-navigation-bottom .nav-right')
    
    if (!navRight) return

    if (canAdvance) {
      if (!nextButton) {
        navRight.innerHTML = `<button class="btn btn-primary" id="step-next-bottom">Next: Descriptions →</button>`
      }
    } else {
      if (nextButton) {
        navRight.innerHTML = ''
      }
    }
  }

  /**
   * Update text snippets ready counter
   */
  public updateTextSnippetsReadyCounter(workflowNav: any, appState: any): void {
    const currentStepName = workflowNav?.getStepName()
    if (currentStepName !== 'description') return

    const selections = appState.getSelections()
    const readyCount = selections.filter((s: any) => {
      // Check current DOM value first (what user is currently typing), then fall back to saved state
      const editedTextarea = document.querySelector(`#edited-text-${s.id}`) as HTMLTextAreaElement
      const currentEditedText = editedTextarea ? editedTextarea.value : ''
      
      const finalText = currentEditedText || s.edited_text || s.extracted_text || ''
      return finalText.trim().length > 0
    }).length

    // Find or create the counter element
    let snippetsReadyElement = document.querySelector('#description-list-card .bg-blue-100')
    if (!snippetsReadyElement) {
      // Create the counter container if it doesn't exist
      const headerDiv = document.querySelector('#description-list-card .card-header > div')
      if (headerDiv) {
        const counterContainer = document.createElement('div')
        counterContainer.className = 'mt-2 flex flex-wrap gap-1'
        counterContainer.innerHTML = `
          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
            ${readyCount} text snippets ready
          </span>
        `
        headerDiv.appendChild(counterContainer)
      }
    } else {
      // Update existing counter
      snippetsReadyElement.textContent = `${readyCount} text snippets ready`
    }
  }

  /**
   * Update selections gallery visibility
   */
  public updateSelectionsGalleryVisibility(appState: any): void {
    const galleryContainer = document.getElementById('selections-gallery-container-main')
    if (!galleryContainer) return

    const selections = appState.getSelections()
    galleryContainer.classList.toggle('hidden', selections.length === 0)
  }
}
